import { DetailRowsSkeleton, FormFieldsSkeleton } from '@/components/core';
import { Card, CardContent } from '@/components/ui-kit/card';
import { DescriptionListSkeleton } from '@/components/ui-kit/description-list';
import { Skeleton } from '@/components/ui-kit/skeleton';

const TabsSkeleton = ({ count }: { count: number }) => (
  <div className="flex gap-2 overflow-hidden">
    {Array.from({ length: count }, (_, index) => (
      <Skeleton key={index} className="h-9 w-28 shrink-0" />
    ))}
  </div>
);

const SectionCardSkeleton = ({ rows }: { rows: number }) => (
  <Card className="flex flex-col gap-3 p-4 shadow-none">
    <Skeleton className="h-4 w-32" />
    <DescriptionListSkeleton rows={rows} />
  </Card>
);

/**
 * `EmployeeProfileView` while the employee loads (LOAD-03): the header, the summary rail beside the tabs
 * and two rows of profile cards.
 */
export const EmployeeProfileSkeleton = () => (
  <div className="flex w-full flex-col gap-6" aria-busy="true">
    <div className="flex items-center gap-4">
      <Skeleton className="size-16 shrink-0 rounded-full" />
      <div className="flex min-w-0 flex-col gap-2">
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-4 w-64 max-w-full" />
        <Skeleton className="h-5 w-16" />
      </div>
    </div>
    <div className="grid items-start gap-6 lg:grid-cols-[280px_minmax(0,1fr)]">
      <Card className="grid gap-4 p-4 shadow-none">
        <DetailRowsSkeleton rows={6} />
      </Card>
      <div className="flex min-w-0 flex-col gap-3">
        <TabsSkeleton count={4} />
        <div className="grid grid-cols-1 gap-4 min-[1180px]:grid-cols-2">
          <SectionCardSkeleton rows={2} />
          <SectionCardSkeleton rows={3} />
          <SectionCardSkeleton rows={5} />
          <SectionCardSkeleton rows={4} />
        </div>
      </div>
    </div>
  </div>
);

/** `EmployeeEditTabs` while the employee (and its lookups) load: the tabs and a tab's fields. */
export const EmployeeEditSkeleton = () => (
  <div className="flex w-full flex-col gap-4" aria-busy="true">
    <TabsSkeleton count={4} />
    <Card>
      <CardContent className="flex flex-col gap-6">
        <FormFieldsSkeleton fields={8} columns={2} />
        <Skeleton className="h-9 w-28 self-end" />
      </CardContent>
    </Card>
  </div>
);
